// src/components/EisenhowerMatrixView.js
// adding a comment just to be able to commit
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { eisenhowerMatrixQueries } from '../repository/graphQueries';
import { formatDate } from '../utils';
import {
    RiInformationLine as InformationCircleIcon,
    RiQuestionLine as QuestionMarkCircleIcon
} from 'react-icons/ri';

// This function takes quadrant-specific thresholds
const getQuadrant = (importance, urgency, quadrantImportanceThreshold, quadrantUrgencyThreshold) => {
    const isImportant = importance >= quadrantImportanceThreshold;
    const isUrgent = urgency >= quadrantUrgencyThreshold;

    if (isUrgent && isImportant) return 'do';
    if (!isUrgent && isImportant) return 'schedule';
    if (isUrgent && !isImportant) return 'delegate';
    return 'eliminate';
};

// Tooltip component for showing explanations
const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    
    return (
        <div className="relative inline-block">
            <div
                className="inline-flex items-center cursor-help"
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
                onClick={() => setIsVisible(!isVisible)} // For mobile support
            >
                {children}
            </div>
            {isVisible && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 p-3 text-sm bg-gray-800 text-white rounded-md shadow-lg z-50">
                    {content}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
};

const Quadrant = ({ title, items, quadrantKey, bgColor, textColor, onCardClick }) => (
    <div className={`rounded-lg shadow-md p-4 ${bgColor} min-h-[200px]`}>
        <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>{title}</h3>
        {items.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No items in this quadrant.</p>
        ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
                {items.map(item => (
                    <div
                        key={item.id}
                        className="bg-white p-3 rounded shadow hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
                        onClick={() => onCardClick(item)}
                        title={`Click to view details for ${item.name}`}
                    >
                        <div className="flex justify-between items-start mb-1">
                            <p className="font-semibold text-sm text-blue-700 truncate flex-1">{item.name}</p>
                            <span className={`text-xs px-2 py-1 rounded ml-2 ${
                                item.type === 'Goal' ? 'bg-blue-100 text-blue-800' :
                                item.type === 'Initiative' ? 'bg-purple-100 text-purple-800' :
                                item.type === 'Issue' ? 'bg-red-100 text-red-800' :
                                item.type === 'ActionItem' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                                {item.type === 'ActionItem' ? 'Action' : item.type}
                            </span>
                        </div>
                        <div className="text-xs text-gray-600">
                            {item.detail1 && (
                                <div className="truncate">
                                    {item.type === 'ActionItem' ? `Assigned: ${item.detail1}` :
                                     item.type === 'Issue' ? `Severity: ${item.detail1}` :
                                     `Status: ${item.detail1}`}
                                </div>
                            )}
                            {item.detail2 && (
                                <div className="truncate">
                                    {item.type === 'ActionItem' ? `Due: ${formatDate(item.detail2)}` :
                                     item.type === 'Goal' ? `Target: ${formatDate(item.detail2)}` :
                                     ''}
                                </div>
                            )}
                            {item.clientId && (
                                <div className="truncate text-gray-500">
                                    Client: {item.clientId}
                                </div>
                            )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 flex justify-between">
                            <span>Importance: {item.importanceScore}</span>
                            <span>Urgency: {item.urgencyScore.toFixed(1)}</span>
                        </div>
                    </div>
                ))}
            </div>
        )}
    </div>
);

const EisenhowerMatrixView = ({ departmentId: propDepartmentId, clientId: propClientId, typeFilter: propTypeFilter = ['Goal', 'Initiative', 'ActionItem', 'Issue'], driFilter: propDriFilter }) => {
    const outletContext = useOutletContext();
    const params = useParams();

    const neo4jService = outletContext?.neo4jService;
    const departmentId = propDepartmentId || outletContext?.departmentId || params.departmentId;
    const clientId = propClientId || outletContext?.clientId || params.clientId;
    const dateRange = outletContext?.dateRange;
    const initialTypeFilter = Array.isArray(propTypeFilter) ? propTypeFilter : (propTypeFilter === 'all' ? ['Goal', 'Initiative', 'ActionItem', 'Issue'] : [propTypeFilter]);


    const navigate = useNavigate();
    const [matrixData, setMatrixData] = useState({ do: [], schedule: [], delegate: [], eliminate: [] });
    const [allItems, setAllItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAdvancedControls, setShowAdvancedControls] = useState(false);

    const [minImportanceFilter, setMinImportanceFilter] = useState(0);
    const [minUrgencyFilter, setMinUrgencyFilter] = useState(0);

    const [quadrantImportanceThreshold, setQuadrantImportanceThreshold] = useState(3);
    const [quadrantUrgencyThreshold, setQuadrantUrgencyThreshold] = useState(2);

    const [displayedItemCount, setDisplayedItemCount] = useState(0);
    const [localTypeFilter, setLocalTypeFilter] = useState(initialTypeFilter);


    const loadMatrixData = useCallback(async () => {
        if (!neo4jService || (!clientId && !departmentId) || !dateRange || !dateRange.start || !dateRange.end) {
            setError("Missing required context (Neo4j service, ID, or date range)");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const queryParams = {
                departmentId: departmentId || null,
                clientId: clientId || null,
                startDate: dateRange.start,
                endDate: dateRange.end,
                driFilter: propDriFilter && propDriFilter.length > 0 ? propDriFilter : null,
                typeFilter: localTypeFilter,
            };
            const results = await neo4jService.runQuery(eisenhowerMatrixQueries.getEisenhowerData, queryParams);
            setAllItems(results || []);
        } catch (err) {
            console.error("Error loading Eisenhower Matrix data:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [neo4jService, clientId, departmentId, dateRange, propDriFilter, localTypeFilter]);

    useEffect(() => {
        loadMatrixData();
    }, [loadMatrixData]);

    useEffect(() => {
        const currentTypeFilters = localTypeFilter;

        const typeFilteredItems = allItems.filter(item =>
            currentTypeFilters.includes(item.type)
        );

        const valueFilteredItems = typeFilteredItems.filter(item =>
            item.importanceScore >= minImportanceFilter &&
            item.urgencyScore >= minUrgencyFilter
        );

        setDisplayedItemCount(valueFilteredItems.length);

        if (valueFilteredItems.length > 0) {
            const categorizedData = { do: [], schedule: [], delegate: [], eliminate: [] };
            valueFilteredItems.forEach(item => {
                const quadrant = getQuadrant(
                    item.importanceScore,
                    item.urgencyScore,
                    quadrantImportanceThreshold,
                    quadrantUrgencyThreshold
                );
                categorizedData[quadrant].push(item);
            });
            setMatrixData(categorizedData);
        } else {
            setMatrixData({ do: [], schedule: [], delegate: [], eliminate: [] });
        }
    }, [allItems, localTypeFilter, minImportanceFilter, minUrgencyFilter, quadrantImportanceThreshold, quadrantUrgencyThreshold]);
    
    useEffect(() => {
        const newFilterArray = Array.isArray(propTypeFilter) ? propTypeFilter : (propTypeFilter === 'all' ? ['Goal', 'Initiative', 'ActionItem', 'Issue'] : [propTypeFilter]);
        setLocalTypeFilter(newFilterArray);
    }, [propTypeFilter]);


    const handleCardClick = (item) => {
    let entityTypePath = '';

    switch(item.type) {
        case 'Goal': entityTypePath = 'goals'; break;
        case 'Initiative': entityTypePath = 'initiatives'; break; // Fixed: was 'goals'
        case 'ActionItem': entityTypePath = 'action-items'; break;
        case 'Issue': entityTypePath = 'issues'; break;
        default: console.warn('Unknown entity type for navigation:', item.type); return;
    }

    // Always navigate to the client context when item has a clientId
    if (item.clientId && entityTypePath) {
        navigate(`/clients/${item.clientId}/${entityTypePath}/${item.id}`);
    } else if (clientId && entityTypePath) { // Fallback to context clientId if no item.clientId
        navigate(`/clients/${clientId}/${entityTypePath}/${item.id}`);
    } else if (departmentId && entityTypePath) { // Only use department route if no client context
        console.warn(`Item ${item.id} has no clientId, cannot navigate to client view`);
        // Optionally show a message to the user
    }
};

    const handleMinImportanceChange = (event) => setMinImportanceFilter(parseInt(event.target.value, 10));
    const handleMinUrgencyChange = (event) => setMinUrgencyFilter(parseFloat(event.target.value));
    const handleQuadrantImportanceChange = (event) => setQuadrantImportanceThreshold(parseInt(event.target.value, 10));
    const handleQuadrantUrgencyChange = (event) => setQuadrantUrgencyThreshold(parseFloat(event.target.value));
    
    const handleLocalTypeFilterChange = (event) => {
        const value = event.target.value;
        if (value === 'all') {
            setLocalTypeFilter(['Goal', 'Initiative', 'ActionItem', 'Issue']);
        } else {
            setLocalTypeFilter(value.split(','));
        }
    };


    const importanceNotches = Array.from({ length: 11 }, (_, i) => i); 
    const urgencyNotches = Array.from({ length: 7 }, (_, i) => i * 0.5); 

    const importanceTooltip = (
        <div>
            <p className="font-medium mb-1">Importance Scale (0-10)</p>
            <p className="mb-2">Based on the number of connections each node has in the graph:</p>
            <ul className="list-disc pl-4 space-y-1">
                <li><b>Higher scores:</b> More connections to other items</li>
                <li><b>Lower scores:</b> Fewer connections</li>
            </ul>
            <p className="mt-2 text-xs text-gray-600">Items with more connections are considered more important as they affect more parts of the system.</p>
        </div>
    );
    
    const urgencyTooltip = (
        <div>
            <p className="font-medium mb-1">Urgency Scale (0-3)</p>
            <p className="mb-2">Calculated differently by item type:</p>
            <ul className="list-disc pl-4 space-y-1">
                <li><b>Issues:</b> Based on severity (High: 3.0, Medium: 2.0, Low: 1.0)</li>
                <li><b>Action Items:</b> Based on deadline (Overdue: 3.0, Due in 7 days: 2.5, Due in 14 days: 2.0)</li>
                <li><b>Goals:</b> Based on target date (Overdue: 3.0, Due in 30 days: 2.0)</li>
                <li><b>Initiatives:</b> Based on status (Blocked: 2.5, Ongoing: 1.5, Planning: 1.0)</li>
            </ul>
        </div>
    );
    
    const quadrantTooltip = (
        <div>
            <p className="font-medium mb-1">Quadrant Scale Thresholds</p>
            <p className="mb-2">These thresholds determine which quadrant items appear in:</p>
            <ul className="list-disc pl-4 space-y-1">
                <li><b>Importance threshold ({quadrantImportanceThreshold}):</b> Number of connections needed to be considered "important"</li>
                <li><b>Urgency threshold ({quadrantUrgencyThreshold.toFixed(1)}):</b> Score needed to be considered "urgent"</li>
            </ul>
            <p className="mt-2 text-xs text-gray-600">Adjusting these thresholds will rebalance how items are distributed across the four quadrants.</p>
        </div>
    );

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
            <span className="ml-2 text-gray-700">Loading Matrix...</span>
        </div>
    );

    if (error) return (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
            Error: {error}
        </div>
    );

    const totalItems = allItems.length;
    const quadrantCounts = {
        do: matrixData.do.length,
        schedule: matrixData.schedule.length,
        delegate: matrixData.delegate.length,
        eliminate: matrixData.eliminate.length
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                <div className="flex items-center">
                    <h2 className="text-xl font-semibold text-gray-800 mr-2">Eisenhower Priority Matrix</h2>
                    <Tooltip content={
                        <div>
                            <p className="font-medium mb-1">The Eisenhower Matrix</p>
                            <p className="mb-2">A prioritization tool that categorizes items based on their importance (connections) and urgency (time sensitivity):</p>
                            <ul className="list-disc pl-4">
                                <li><b>Do First:</b> High connections & high time sensitivity (urgency ≥ {quadrantUrgencyThreshold.toFixed(1)}, importance ≥ {quadrantImportanceThreshold})</li>
                                <li><b>Schedule:</b> High connections but lower time sensitivity (urgency &lt; {quadrantUrgencyThreshold.toFixed(1)}, importance ≥ {quadrantImportanceThreshold})</li>
                                <li><b>Delegate:</b> Lower connections but high time sensitivity (urgency ≥ {quadrantUrgencyThreshold.toFixed(1)}, importance &lt; {quadrantImportanceThreshold})</li>
                                <li><b>Eliminate:</b> Lower connections and lower time sensitivity (urgency &lt; {quadrantUrgencyThreshold.toFixed(1)}, importance &lt; {quadrantImportanceThreshold})</li>
                            </ul>
                        </div>
                    }>
                        <QuestionMarkCircleIcon className="h-5 w-5 text-blue-600" />
                    </Tooltip>
                </div>
                
                <div className="flex items-center">
                    <label htmlFor="localTypeFilter" className="text-sm font-medium text-gray-700 mr-2 whitespace-nowrap">Type:</label>
                    <select
                        id="localTypeFilter"
                        value={localTypeFilter.join(',')}
                        onChange={handleLocalTypeFilterChange}
                        className="block w-full px-2 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="Goal,Initiative,ActionItem,Issue">All Types</option>
                        <option value="Goal">Goals</option>
                        <option value="Initiative">Initiatives</option>
                        <option value="ActionItem">Action Items</option>
                        <option value="Issue">Issues</option>
                        <option value="Goal,Initiative">Goals & Initiatives</option>
                    </select>
                </div>
            </div>

            <div className="mb-6 p-4 border border-gray-200 rounded-lg shadow-sm bg-white">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-md font-semibold text-gray-700">Display Filters</h3>
                    <button 
                        onClick={() => setShowAdvancedControls(!showAdvancedControls)}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                        {showAdvancedControls ? 'Hide Advanced Controls' : 'Show Advanced Controls'}
                    </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    <div className="flex items-center">
                        <div className="flex-1">
                            <div className="flex items-center mb-1">
                                <label htmlFor="minImportanceFilter" className="block text-sm font-medium text-gray-700 mr-1">
                                    Min. Importance ≥ <span className="font-semibold text-blue-600">{minImportanceFilter}</span>
                                </label>
                                <Tooltip content={importanceTooltip}>
                                    <InformationCircleIcon className="h-4 w-4 text-gray-500" />
                                </Tooltip>
                            </div>
                            <input
                                type="range" id="minImportanceFilter"
                                min="0" max="10" step="1" value={minImportanceFilter}
                                onChange={handleMinImportanceChange}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                list="importance-markers"
                            />
                            <datalist id="importance-markers">
                                {importanceNotches.map(val => <option key={`imp-marker-${val}`} value={val}></option>)}
                            </datalist>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <div className="flex-1">
                            <div className="flex items-center mb-1">
                                <label htmlFor="minUrgencyFilter" className="block text-sm font-medium text-gray-700 mr-1">
                                    Min. Urgency ≥ <span className="font-semibold text-red-600">{minUrgencyFilter.toFixed(1)}</span>
                                </label>
                                <Tooltip content={urgencyTooltip}>
                                    <InformationCircleIcon className="h-4 w-4 text-gray-500" />
                                </Tooltip>
                            </div>
                            <input
                                type="range" id="minUrgencyFilter"
                                min="0" max="3" step="0.5" value={minUrgencyFilter}
                                onChange={handleMinUrgencyChange}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                                list="urgency-markers"
                            />
                            <datalist id="urgency-markers">
                                {urgencyNotches.map(val => <option key={`urg-marker-${val}`} value={val.toFixed(1)}></option>)}
                            </datalist>
                        </div>
                    </div>
                </div>
            </div>

            {showAdvancedControls && (
                <div className="mb-6 p-4 border border-gray-200 rounded-lg shadow-sm bg-white">
                    <div className="flex items-center mb-3">
                        <h3 className="text-md font-semibold text-gray-700 mr-1">Quadrant Scale</h3>
                        <Tooltip content={quadrantTooltip}>
                            <InformationCircleIcon className="h-4 w-4 text-gray-500" />
                        </Tooltip>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <div>
                            <label htmlFor="quadrantImportanceThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                                Important if Score ≥ <span className="font-semibold text-green-600">{quadrantImportanceThreshold}</span>
                            </label>
                            <input
                                type="range" id="quadrantImportanceThreshold"
                                min="0" max="10" step="1" value={quadrantImportanceThreshold}
                                onChange={handleQuadrantImportanceChange}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                                list="importance-markers-quad"
                            />
                            <datalist id="importance-markers-quad">
                                {importanceNotches.map(val => <option key={`q-imp-marker-${val}`} value={val}></option>)}
                            </datalist>
                        </div>
                        <div>
                            <label htmlFor="quadrantUrgencyThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                                Urgent if Score ≥ <span className="font-semibold text-orange-600">{quadrantUrgencyThreshold.toFixed(1)}</span>
                            </label>
                            <input
                                type="range" id="quadrantUrgencyThreshold"
                                min="0" max="3" step="0.5" value={quadrantUrgencyThreshold}
                                onChange={handleQuadrantUrgencyChange}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
                                list="urgency-markers-quad"
                            />
                            <datalist id="urgency-markers-quad">
                                {urgencyNotches.map(val => <option key={`q-urg-marker-${val}`} value={val.toFixed(1)}></option>)}
                            </datalist>
                        </div>
                    </div>
                </div>
            )}

            {displayedItemCount > 0 ? (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-600">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 justify-between items-center">
                        <div>
                            <span className="font-bold">{displayedItemCount}</span> items displayed
                            {totalItems > displayedItemCount && (
                                <span className="text-gray-500 ml-1">
                                    (filtered from {totalItems} total)
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-x-3">
                            <span className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-red-500 mr-1"></span>
                                Do: <span className="font-semibold ml-1">{quadrantCounts.do}</span>
                            </span>
                            <span className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-blue-500 mr-1"></span>
                                Schedule: <span className="font-semibold ml-1">{quadrantCounts.schedule}</span>
                            </span>
                            <span className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-yellow-500 mr-1"></span>
                                Delegate: <span className="font-semibold ml-1">{quadrantCounts.delegate}</span>
                            </span>
                            <span className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-gray-500 mr-1"></span>
                                Eliminate: <span className="font-semibold ml-1">{quadrantCounts.eliminate}</span>
                            </span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                    No items match the current filter criteria. Try adjusting the display filters or selected item type.
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <Quadrant
                    title="Do First (Urgent & Important)"
                    items={matrixData.do} quadrantKey="do"
                    bgColor="bg-red-50" textColor="text-red-700"
                    onCardClick={handleCardClick}
                />
                <Quadrant
                    title="Schedule (Important, Not Urgent)"
                    items={matrixData.schedule} quadrantKey="schedule"
                    bgColor="bg-blue-50" textColor="text-blue-700"
                    onCardClick={handleCardClick}
                />
                <Quadrant
                    title="Delegate (Urgent, Not Important)"
                    items={matrixData.delegate} quadrantKey="delegate"
                    bgColor="bg-yellow-50" textColor="text-yellow-700"
                    onCardClick={handleCardClick}
                />
                <Quadrant
                    title="Eliminate/Delay (Not Urgent, Not Important)"
                    items={matrixData.eliminate} quadrantKey="eliminate"
                    bgColor="bg-gray-50" textColor="text-gray-700"
                    onCardClick={handleCardClick}
                />
            </div>
        </div>
    );
};

export default EisenhowerMatrixView;